// ExcelViewerDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import * as XLSX from 'xlsx';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateDataGridSx,
} from '../../../theme/harmonateStyles';

const ExcelViewerDialog = ({ open, onClose, fileInstanceId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    if (open && fileInstanceId) {
      fetchAndDisplayExcel();
    }
  }, [open, fileInstanceId]);

  const fetchAndDisplayExcel = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Step 1: Get presigned URL (same as PDF viewer)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileInstanceId}/presigned-url`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch presigned URL: ${response.status}`);
      }

      const data = await response.json();
      const presignedUrl = data.url;

      if (!presignedUrl) {
        throw new Error('No URL returned from API');
      }

      // Step 2: Fetch the Excel file
      const fileResponse = await fetch(presignedUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to fetch Excel file');
      }

      const arrayBuffer = await fileResponse.arrayBuffer();

      // Step 3: Parse Excel file with SheetJS
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,  // Return array of arrays
        defval: '', // Default value for empty cells
        raw: false  // Format cells as strings
      });

      if (jsonData.length === 0) {
        throw new Error('Excel file is empty');
      }

      // Step 4: Create columns from first row (headers)
      const headers = jsonData[0];
      const gridColumns = headers.map((header, index) => ({
        field: `col_${index}`,
        headerName: header || `Column ${index + 1}`,
        width: 150,
        editable: false,
      }));

      // Step 5: Create rows from remaining data
      const gridRows = jsonData.slice(1).map((row, rowIndex) => {
        const rowData = { id: rowIndex };
        row.forEach((cell, colIndex) => {
          rowData[`col_${colIndex}`] = cell || '';
        });
        return rowData;
      });

      setColumns(gridColumns);
      setRows(gridRows);
      setFileName(data.fileName || 'Excel File');

    } catch (err) {
      console.error('Error loading Excel file:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRows([]);
    setColumns([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          ...harmonateDialogPaperProps.sx,
          height: '90vh',
        }
      }}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
          {fileName}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={harmonateDialogCloseIconSx}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        ...harmonateDialogContentSx,
        p: 0,
        pt: '0 !important',
        display: 'flex', 
        flexDirection: 'column',
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <CircularProgress sx={{ color: ht.blue }} />
            <Typography sx={{ ml: 2 }}>Loading Excel file...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : rows.length > 0 ? (
          <Box sx={{ p: 2, width: '100%', height: '100%', boxSizing: 'border-box' }}>
            <DataGridPro
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 50 },
                },
              }}
              pageSizeOptions={[25, 50, 100]}
              disableColumnMenu
              disableDensitySelector
              autoHeight={false}
              sx={{
                ...harmonateDataGridSx,
                '& .MuiDataGrid-virtualScroller': {
                  ...harmonateDataGridSx['& .MuiDataGrid-virtualScroller'],
                  overflowX: 'auto',
                  minHeight: '200px',
                },
                '& .MuiDataGrid-main': {
                  margin: 0,
                  width: '100%',
                }
              }}
            />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <Typography color="text.secondary">
              No data to display
            </Typography>
          </Box>
        )}
      </DialogContent>

    </Dialog>
  );
};

export default ExcelViewerDialog;
